import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Home,
  X,
} from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from './ui/Input';
import useLocale from '../contexts/LocaleContext';
import { useLocationActivityRanking } from '../hooks/useAnalytics';
import { useFacilityTypes } from '../hooks/useFacilities';
import { exportToExcel } from '../lib/excel-export';
import { cn } from '../lib/utils';
import type { LocationActivityRankingItemDto } from '../types/api';

const LOCATION_TYPES = ['PROVINCE', 'DISTRICT', 'SECTOR', 'CELL', 'VILLAGE'] as const;

const CHILD_TYPE: Record<string, string> = {
  PROVINCE: 'DISTRICT',
  DISTRICT: 'SECTOR',
  SECTOR: 'CELL',
  CELL: 'VILLAGE',
  VILLAGE: 'VILLAGE',
};

interface BreadcrumbItem {
  id: string;
  name: string;
  type: string;
}

interface LocationActivityRankingProps {
  type?: 'HYGIENE' | 'COMPLIANCE';
}

function getRankBadgeColor(rank: number): string {
  if (rank === 1) return 'bg-yellow-100 text-yellow-800';
  if (rank === 2) return 'bg-gray-100 text-gray-800';
  if (rank === 3) return 'bg-orange-100 text-orange-800';
  return 'bg-secondary text-primary';
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-amber-600';
  if (score >= 40) return 'text-orange-500';
  return 'text-red-500';
}

function getCoverageColor(coverage: number): string {
  if (coverage >= 70) return 'text-green-600';
  if (coverage >= 40) return 'text-amber-600';
  return 'text-red-500';
}

/** Inline SVG sparkline bar chart for bucket trends. */
function BucketSparkline({ buckets }: { buckets: LocationActivityRankingItemDto['buckets'] }) {
  if (!buckets.length) return <span className='text-gray-300 text-xs'>—</span>;

  const max = Math.max(...buckets.map(b => b.assessments), 1);
  const width = 64;
  const height = 20;
  const barW = Math.max(2, Math.floor((width - (buckets.length - 1)) / buckets.length));
  const gap = buckets.length > 1 ? (width - barW * buckets.length) / (buckets.length - 1) : 0;

  return (
    <svg width={width} height={height} aria-hidden='true' className='flex-shrink-0'>
      {buckets.map((b, i) => {
        const barH = Math.max(2, Math.round((b.assessments / max) * height));
        const x = i * (barW + gap);
        const y = height - barH;
        return (
          <rect
            key={b.label}
            x={x}
            y={y}
            width={barW}
            height={barH}
            fill='var(--nhip-green)'
            rx={1}
          >
            <title>{`${b.label}: ${b.assessments}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

function LocationActivityRanking({ type = 'HYGIENE' }: LocationActivityRankingProps) {
  const { dictionary } = useLocale();

  const [granularity, setGranularity] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [locationType, setLocationType] = useState('DISTRICT');
  const [facilityTypeId, setFacilityTypeId] = useState('');
  const [page, setPage] = useState(1);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [parentGeoLocationId, setParentGeoLocationId] = useState<string | undefined>();

  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportLoading, setExportLoading] = useState<string | null>(null);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  const LIMIT = 20;

  const queryParams = useMemo(() => ({
    granularity,
    from: from || undefined,
    to: to || undefined,
    type,
    locationType,
    parentGeoLocationId,
    facilityTypeId: facilityTypeId || undefined,
    limit: LIMIT,
    page,
  }), [granularity, from, to, type, locationType, parentGeoLocationId, facilityTypeId, page]);

  const { data, isLoading, error } = useLocationActivityRanking(queryParams);
  const { data: facilityTypes } = useFacilityTypes();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };
    if (showExportDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportDropdown]);

  const handleGranularityChange = useCallback((g: 'daily' | 'weekly' | 'monthly') => {
    setGranularity(g);
    setPage(1);
  }, []);

  const handleDrillDown = useCallback((item: LocationActivityRankingItemDto) => {
    if (item.locationType === 'VILLAGE') return;
    setBreadcrumb(prev => [...prev, { id: item.geoLocationId, name: item.locationName, type: item.locationType }]);
    setParentGeoLocationId(item.geoLocationId);
    setLocationType(CHILD_TYPE[item.locationType] || 'DISTRICT');
    setPage(1);
  }, []);

  const handleBreadcrumbClick = useCallback((index: number) => {
    if (index === -1) {
      setBreadcrumb([]);
      setParentGeoLocationId(undefined);
      setLocationType('DISTRICT');
    } else {
      const slice = breadcrumb.slice(0, index + 1);
      setBreadcrumb(slice);
      const target = slice[slice.length - 1];
      setParentGeoLocationId(target.id);
      setLocationType(CHILD_TYPE[target.type] || 'DISTRICT');
    }
    setPage(1);
  }, [breadcrumb]);

  const clearFilters = useCallback(() => {
    setFrom('');
    setTo('');
    setLocationType('DISTRICT');
    setFacilityTypeId('');
    setPage(1);
    setBreadcrumb([]);
    setParentGeoLocationId(undefined);
  }, []);

  const totalPages = data ? Math.ceil(data.total / LIMIT) : 0;

  const handleExportExcel = useCallback(() => {
    if (!data?.items?.length) return;
    setExportLoading('excel');
    setShowExportDropdown(false);
    try {
      const rows = data.items.map(item => ({
        rank: item.rank,
        locationName: item.locationName,
        locationType: item.locationType,
        parentName: item.parentName ?? '—',
        totalAssessments: item.totalAssessments,
        firstAssessments: item.firstAssessments,
        revisits: item.revisits,
        coverageRate: `${item.coverageRate.toFixed(1)}%`,
        averageScore: `${item.averageScore.toFixed(1)}%`,
        totalFacilities: item.totalFacilities,
      }));
      exportToExcel({
        fileName: `Activity_Ranking_${data.from}_${data.to}`,
        sheetName: 'Activity Ranking',
        data: rows,
        columns: [
          { key: 'rank', label: 'Rank' },
          { key: 'locationName', label: 'Location' },
          { key: 'locationType', label: 'Type' },
          { key: 'parentName', label: 'Parent' },
          { key: 'totalAssessments', label: 'Total Assessments' },
          { key: 'firstAssessments', label: 'First Assessments' },
          { key: 'revisits', label: 'Revisits' },
          { key: 'coverageRate', label: 'Coverage Rate' },
          { key: 'averageScore', label: 'Avg Score' },
          { key: 'totalFacilities', label: 'Total Facilities' },
        ],
        title: 'NHIP — Location Activity Ranking',
        subtitle: `${data.locationType} | ${data.from} – ${data.to} | ${data.granularity}`,
      });
    } catch (err) {
      console.error('Excel export failed:', err);
    } finally {
      setExportLoading(null);
    }
  }, [data]);

  const handleExportPDF = useCallback(() => {
    if (!data?.items?.length) return;
    setExportLoading('pdf');
    setShowExportDropdown(false);
    try {
      const doc = new jsPDF();
      const margin = 20;
      let y = 20;

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('NHIP — Location Activity Ranking', margin, y);
      y += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`${data.locationType} | ${data.from} – ${data.to} | ${data.granularity}`, margin, y);
      y += 8;
      doc.text(`${data.total} locations`, margin, y);
      y += 12;

      autoTable(doc, {
        startY: y,
        head: [['Rank', 'Location', 'Type', 'Parent', 'Total', 'First', 'Revisits', 'Coverage', 'Avg Score']],
        body: data.items.map(item => [
          item.rank.toString(),
          item.locationName,
          item.locationType,
          item.parentName ?? '—',
          item.totalAssessments.toString(),
          item.firstAssessments.toString(),
          item.revisits.toString(),
          `${item.coverageRate.toFixed(1)}%`,
          `${item.averageScore.toFixed(1)}%`,
        ]),
        margin: { left: margin, right: margin },
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [27, 107, 69] },
      });

      const timestamp = new Date().toISOString().split('T')[0];
      doc.save(`Activity_Ranking_${data.from}_${data.to}_${timestamp}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExportLoading(null);
    }
  }, [data]);

  const items = data?.items ?? [];

  return (
    <div className='bg-white rounded-lg shadow-sm p-4 sm:p-6 space-y-4'>
      {/* Header */}
      <div className='flex items-start justify-between gap-3 flex-wrap'>
        <div>
          <h2 className='flex items-center gap-2 text-base sm:text-lg font-semibold text-gray-900'>
            <Activity className='h-4 w-4 sm:h-5 sm:w-5 text-primary' />
            {dictionary['activity_ranking.title'] ?? 'Location Activity Ranking'}
          </h2>
          {data && (
            <p className='text-xs text-gray-500 mt-0.5'>
              {data.from} – {data.to} · {data.total} {dictionary['objective_analytics.locations'] ?? 'locations'}
            </p>
          )}
        </div>

        {/* Export dropdown */}
        <div className='relative flex-shrink-0' ref={exportDropdownRef}>
          <button
            onClick={() => setShowExportDropdown(prev => !prev)}
            disabled={!items.length}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border font-medium transition-colors',
              items.length
                ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'border-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            <Download className='h-3.5 w-3.5' />
            {dictionary['common.export'] ?? 'Export'}
          </button>
          {showExportDropdown && (
            <div className='absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10'>
              <button
                onClick={handleExportExcel}
                disabled={exportLoading === 'excel'}
                className='flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg'
              >
                <FileText className='h-3.5 w-3.5 text-green-600' />
                {exportLoading === 'excel' ? 'Exporting…' : 'Excel (.xlsx)'}
              </button>
              <button
                onClick={handleExportPDF}
                disabled={exportLoading === 'pdf'}
                className='flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg'
              >
                <FileText className='h-3.5 w-3.5 text-red-600' />
                {exportLoading === 'pdf' ? 'Exporting…' : 'PDF (.pdf)'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className='flex flex-wrap gap-3 items-end'>
        {/* Granularity toggle */}
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>
            {dictionary['activity_ranking.granularity'] ?? 'Granularity'}
          </label>
          <div className='flex rounded-md border border-gray-300 overflow-hidden text-xs'>
            {(['daily', 'weekly', 'monthly'] as const).map(g => (
              <button
                key={g}
                onClick={() => handleGranularityChange(g)}
                className={cn(
                  'px-3 py-1.5 font-medium capitalize transition-colors',
                  granularity === g ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
                )}
              >
                {dictionary[`activity_ranking.${g}`] ?? g}
              </button>
            ))}
          </div>
        </div>

        {/* Date from */}
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>
            {dictionary['common.from'] ?? 'From'}
          </label>
          <Input
            type='date'
            value={from}
            onChange={e => { setFrom(e.target.value); setPage(1); }}
            className='w-auto text-sm'
          />
        </div>

        {/* Date to */}
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>
            {dictionary['common.to'] ?? 'To'}
          </label>
          <Input
            type='date'
            value={to}
            onChange={e => { setTo(e.target.value); setPage(1); }}
            className='w-auto text-sm'
          />
        </div>

        {/* Location type */}
        {!breadcrumb.length && (
          <div>
            <label className='block text-xs font-medium text-gray-700 mb-1'>
              {dictionary['location_ranking.location_type'] ?? 'Location Type'}
            </label>
            <Input
              type='select'
              value={locationType}
              onChange={e => { setLocationType(e.target.value as string); setPage(1); }}
              options={LOCATION_TYPES.map(t => ({ value: t, label: t.charAt(0) + t.slice(1).toLowerCase() }))}
              className='w-auto min-w-[120px] text-sm'
            />
          </div>
        )}

        {/* Facility type */}
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-1'>
            {dictionary['location_ranking.facility_type'] ?? 'Facility Type'}
          </label>
          <Input
            type='select'
            value={facilityTypeId}
            onChange={e => { setFacilityTypeId(e.target.value as string); setPage(1); }}
            placeholder={dictionary['location_ranking.all_types'] ?? 'All types'}
            options={(facilityTypes ?? []).map((ft: { id: string; name: string }) => ({ value: ft.id, label: ft.name }))}
            className='w-auto min-w-[140px] text-sm'
          />
        </div>

        {/* Clear filters */}
        {(from || to || facilityTypeId || breadcrumb.length > 0 || locationType !== 'DISTRICT') && (
          <button
            onClick={clearFilters}
            className='flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors'
          >
            <X className='h-3 w-3' />
            {dictionary['location_ranking.clear_filters'] ?? 'Clear'}
          </button>
        )}
      </div>

      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <nav className='flex items-center gap-1 text-xs text-gray-500 flex-wrap'>
          <button
            onClick={() => handleBreadcrumbClick(-1)}
            className='flex items-center gap-1 hover:text-primary transition-colors'
          >
            <Home className='h-3 w-3' />
            <span>National</span>
          </button>
          {breadcrumb.map((crumb, i) => (
            <span key={crumb.id} className='flex items-center gap-1'>
              <ChevronRight className='h-3 w-3' />
              {i === breadcrumb.length - 1 ? (
                <span className='text-gray-900 font-medium'>{crumb.name}</span>
              ) : (
                <button
                  onClick={() => handleBreadcrumbClick(i)}
                  className='hover:text-primary transition-colors'
                >
                  {crumb.name}
                </button>
              )}
            </span>
          ))}
        </nav>
      )}

      {/* Table */}
      {error ? (
        <div className='flex items-center justify-center h-40 text-center'>
          <div>
            <p className='text-red-600 font-medium mb-1'>Failed to load activity ranking</p>
            <p className='text-xs text-gray-500'>{(error as Error).message}</p>
          </div>
        </div>
      ) : isLoading ? (
        <div className='space-y-2'>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className='h-10 bg-gray-100 rounded animate-pulse' />
          ))}
        </div>
      ) : !items.length ? (
        <div className='flex items-center justify-center h-40 text-gray-500 text-sm'>
          {dictionary['activity_ranking.no_data'] ?? 'No locations found for the selected filters.'}
        </div>
      ) : (
        <div className='overflow-x-auto -mx-4 sm:-mx-6'>
          <table className='w-full text-sm min-w-[700px]'>
            <thead>
              <tr className='border-b border-gray-200 text-left'>
                <th className='px-4 sm:px-6 pb-2 font-medium text-gray-500 text-xs w-12'>
                  {dictionary['objective_analytics.rank'] ?? 'Rank'}
                </th>
                <th className='px-2 pb-2 font-medium text-gray-500 text-xs'>
                  {dictionary['objective_analytics.location'] ?? 'Location'}
                </th>
                <th className='px-2 pb-2 font-medium text-gray-500 text-xs hidden sm:table-cell'>
                  {dictionary['activity_ranking.parent'] ?? 'Parent'}
                </th>
                <th className='px-2 pb-2 font-medium text-gray-500 text-xs text-right'>
                  {dictionary['activity_ranking.total_assessments'] ?? 'Total'}
                </th>
                <th className='px-2 pb-2 font-medium text-gray-500 text-xs text-right hidden md:table-cell'>
                  {dictionary['activity_ranking.first_assessments'] ?? 'First'}
                </th>
                <th className='px-2 pb-2 font-medium text-gray-500 text-xs text-right hidden md:table-cell'>
                  {dictionary['activity_ranking.revisits'] ?? 'Revisits'}
                </th>
                <th className='px-2 pb-2 font-medium text-gray-500 text-xs text-right hidden lg:table-cell'>
                  {dictionary['activity_ranking.coverage_rate'] ?? 'Coverage'}
                </th>
                <th className='px-2 pb-2 font-medium text-gray-500 text-xs text-right hidden lg:table-cell'>
                  {dictionary['activity_ranking.avg_score'] ?? 'Avg Score'}
                </th>
                <th className='px-2 pb-2 font-medium text-gray-500 text-xs hidden xl:table-cell'>
                  {dictionary['activity_ranking.trend'] ?? 'Trend'}
                </th>
                <th className='px-4 sm:px-6 pb-2 w-8' />
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {items.map(item => (
                <tr
                  key={item.geoLocationId}
                  className={cn(
                    'hover:bg-gray-50 transition-colors',
                    item.locationType !== 'VILLAGE' && 'cursor-pointer'
                  )}
                  onClick={() => handleDrillDown(item)}
                >
                  <td className='px-4 sm:px-6 py-3'>
                    <span className={cn(
                      'inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold',
                      getRankBadgeColor(item.rank)
                    )}>
                      {item.rank}
                    </span>
                  </td>
                  <td className='px-2 py-3'>
                    <div className='font-medium text-gray-900 leading-tight'>{item.locationName}</div>
                    <div className='text-xs text-gray-400'>{item.locationType}</div>
                  </td>
                  <td className='px-2 py-3 text-xs text-gray-500 hidden sm:table-cell'>
                    {item.parentName ?? '—'}
                  </td>
                  <td className='px-2 py-3 text-right font-semibold text-gray-900'>
                    {item.totalAssessments.toLocaleString()}
                  </td>
                  <td className='px-2 py-3 text-right text-xs text-gray-600 hidden md:table-cell'>
                    {item.firstAssessments.toLocaleString()}
                  </td>
                  <td className='px-2 py-3 text-right text-xs text-gray-600 hidden md:table-cell'>
                    {item.revisits.toLocaleString()}
                  </td>
                  <td className='px-2 py-3 text-right hidden lg:table-cell'>
                    <div className='flex items-center justify-end gap-1.5'>
                      <span className={cn('text-xs font-medium', getCoverageColor(item.coverageRate))}>
                        {item.coverageRate.toFixed(1)}%
                      </span>
                      <div className='w-12 bg-gray-200 rounded-full h-1.5 flex-shrink-0'>
                        <div
                          className={cn(
                            'h-1.5 rounded-full',
                            item.coverageRate >= 70 ? 'bg-green-500'
                              : item.coverageRate >= 40 ? 'bg-amber-500'
                              : 'bg-red-500'
                          )}
                          style={{ width: `${Math.min(item.coverageRate, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className='px-2 py-3 text-right hidden lg:table-cell'>
                    <span className={cn('text-xs font-semibold', getScoreColor(item.averageScore))}>
                      {item.averageScore.toFixed(1)}%
                    </span>
                  </td>
                  <td className='px-2 py-3 hidden xl:table-cell'>
                    <BucketSparkline buckets={item.buckets} />
                  </td>
                  <td className='px-4 sm:px-6 py-3'>
                    {item.locationType !== 'VILLAGE' ? (
                      <ChevronRight className='h-4 w-4 text-gray-400' />
                    ) : (
                      <span className='text-gray-200'>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between pt-2 border-t border-gray-100'>
          <span className='text-xs text-gray-500'>
            Page {page} of {totalPages} · {data?.total} total
          </span>
          <div className='flex gap-2'>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className={cn(
                'flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border transition-colors font-medium',
                page <= 1
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              )}
            >
              <ChevronLeft className='h-3 w-3' />
              Prev
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className={cn(
                'flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border transition-colors font-medium',
                page >= totalPages
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              )}
            >
              Next
              <ChevronRight className='h-3 w-3' />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(LocationActivityRanking);
